import { FormEvent, useRef } from 'react';

interface Step5Props {
  data: Record<string, string>;
  onChange: (field: string, value: string) => void;
  onNext: () => void;
  onBack: () => void;
}

const PULL_OPTIONS = [
  {
    key: 'pullCopy',
    label: 'Pull written copy from my listing',
    desc: 'We\'ll draft the text and send it for your approval before anything goes live.',
  },
  {
    key: 'pullPhotos',
    label: 'Pull photos from my listing',
    desc: 'We\'ll curate a selection and confirm with you before publishing.',
  },
  {
    key: 'pullAmenities',
    label: 'Pull amenities and property details',
    desc: 'We\'ll review and clean up the list — you approve the final version.',
  },
];

export default function Step5Photos({ data, onChange, onNext, onBack }: Step5Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onNext();
  };

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    const names = Array.from(files).map((f) => f.name).join(', ');
    onChange('uploadedFileNames', names);
  };

  const togglePull = (key: string) => {
    const current = data[key];
    onChange(key, current === 'false' ? 'true' : 'false');
  };

  const isChecked = (key: string) => data[key] !== 'false';

  return (
    <div className="w-full max-w-xl">
      <p className="text-[#b6862d] text-xs tracking-widest uppercase font-sans mb-5">Step 5 of 6</p>
      <h2 className="font-serif text-4xl md:text-5xl text-[#141413] mb-3 leading-tight">Photos and content.</h2>
      <p className="text-[#77776f] text-sm font-sans leading-relaxed mb-10">
        The court photo must be one of the first three submitted.
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-7">
        {/* Photo upload */}
        <div className="flex flex-col gap-2.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans">Upload Photos</label>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="w-full flex flex-col items-center justify-center gap-3 px-6 py-8 rounded-xl border-2 border-dashed border-[#e0d9ce] bg-white hover:border-[#b6862d] transition-colors cursor-pointer"
          >
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#f2ece2]">
              <i className="ri-upload-cloud-2-line text-2xl text-[#b6862d]" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium font-sans text-[#3a3a34]">
                {data.uploadedFileNames ? 'Files selected' : 'Click to upload photos'}
              </p>
              <p className="text-xs font-sans text-[#77776f] mt-1">
                {data.uploadedFileNames || 'JPG, PNG up to 10MB each. Court photo must be in the first three.'}
              </p>
            </div>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
          {data.uploadedFileNames && (
            <p className="text-xs font-sans text-[#234012]">
              <i className="ri-check-line mr-1" />
              {data.uploadedFileNames.split(',').length} file{data.uploadedFileNames.split(',').length > 1 ? 's' : ''} selected
            </p>
          )}
        </div>

        {/* Drive/Dropbox link */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans flex items-center gap-2">
            Dropbox or Drive Link
            <span className="text-[#77776f] text-[10px] normal-case tracking-normal font-sans">optional</span>
          </label>
          <input
            type="url"
            name="mediaLink"
            value={data.mediaLink || ''}
            onChange={(e) => onChange('mediaLink', e.target.value)}
            className="bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors"
            placeholder="https://drive.google.com/drive/folders/..."
          />
          <p className="text-[#77776f] text-xs font-sans">Share a folder link for bulk uploads — videos welcome too.</p>
        </div>

        {/* Pull options — checked by default */}
        <div className="flex flex-col gap-2.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans">Content Permissions</label>
          <div className="flex flex-col gap-2">
            {PULL_OPTIONS.map((opt) => {
              const checked = isChecked(opt.key);
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => togglePull(opt.key)}
                  className={`w-full flex items-start gap-4 px-5 py-4 rounded-xl border-2 transition-all duration-200 cursor-pointer text-left ${
                    checked
                      ? 'bg-[#234012]/5 border-[#234012]'
                      : 'bg-white border-[#e0d9ce] hover:border-[#b6862d]'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all ${
                    checked ? 'bg-[#234012] border-[#234012]' : 'border-[#d0c9be]'
                  }`}>
                    {checked && <i className="ri-check-line text-[10px] text-white" />}
                  </div>
                  <div>
                    <p className={`text-sm font-medium font-sans ${checked ? 'text-[#234012]' : 'text-[#3a3a34]'}`}>
                      {opt.label}
                    </p>
                    <p className="text-xs font-sans text-[#77776f] mt-1 leading-relaxed">{opt.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-4 mt-2">
          <button type="button" onClick={onBack} className="text-sm font-sans text-[#77776f] hover:text-[#3a3a34] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5">
            <i className="ri-arrow-left-line" /> Back
          </button>
          <button
            type="submit"
            className="bg-[#234012] text-[#fcfbf7] text-sm font-sans font-medium px-8 py-4 rounded-lg hover:bg-[#305a18] transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );
}
